import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../contexts/SidebarContext';
import { useAdmin } from '../contexts/AdminContext';
import { useMacReleaseInfo } from '../hooks/useMacReleaseInfo';
import { useWindowsReleaseInfo } from '../hooks/useWindowsReleaseInfo';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import {
  Monitor,
  Download,
  Puzzle,
  ExternalLink,
  PlayCircle,
  ChevronDown,
  ArrowRight,
  Save,
  Loader2,
  AlertTriangle,
  ShieldAlert,
  Sparkles,
  BookOpen,
  Pencil,
  Trash2,
  Plus,
  X,
} from 'lucide-react';

// ── API base (Vite) ──
const API_BASE =
  import.meta.env.VITE_BACKEND_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

// Cores de tema p/ accordions — espelham os tokens do index.css
// Padrão premium: acentos de seção TODOS no azul da marca (sem laranja/verde).
const THEME = { blue: '#0DAEFF', orange: '#0DAEFF', green: '#0DAEFF' };

// ── hex + alpha → rgba ──
const hexA = (hex, a) => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

// ── Estilo de card padrão DarkPlanner ──
const CARD_STYLE = {
  background: 'var(--dp-bg-card)',
  border: '1px solid var(--dp-border-subtle)',
  borderRadius: 'var(--dp-radius-md, 12px)',
};

// ── Apple glyph (lucide não tem ícone Apple) ──
const AppleGlyph = ({ size = 18, color = 'var(--dp-text-secondary)' }) => (
  <svg viewBox="0 0 814 1000" xmlns="http://www.w3.org/2000/svg" style={{ width: size, height: size, flexShrink: 0 }}>
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" fill={color} />
  </svg>
);

// ── Inline code chip ──
const Code = ({ children }) => (
  <code style={{
    background: 'var(--dp-blue-subtle)',
    padding: '1px 6px',
    borderRadius: 'var(--dp-radius-xs, 4px)',
    fontSize: '11px',
    color: 'var(--dp-text-primary)',
  }}>
    {children}
  </code>
);

// ── Badge ──
const Badge = ({ children, color = THEME.blue }) => (
  <span style={{
    fontSize: '12px',
    fontWeight: 500,
    color,
    background: hexA(color, 0.12),
    padding: '6px 12px',
    borderRadius: 'var(--dp-radius-full, 9999px)',
    border: `1px solid ${hexA(color, 0.25)}`,
    letterSpacing: '0.3px',
    display: 'inline-block',
  }}>
    {children}
  </span>
);

// ── Extrai o ID de um vídeo do YouTube ──
const extractYouTubeId = (val) => {
  if (!val) return null;
  const s = String(val).trim();
  const patterns = [
    /[?&]v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = s.match(p);
    if (m) return m[1];
  }
  if (/^[a-zA-Z0-9_-]{11}$/.test(s)) return s;
  return null;
};

// ── Player de vídeo (ou placeholder) — limitado a 720px ──
const VideoFrame = ({ videoId, loading, title, radius = 12 }) => {
  if (videoId) {
    return (
      <div style={{
        position: 'relative', width: '100%', maxWidth: '720px', margin: '0 auto', aspectRatio: '16 / 9',
        borderRadius: `${radius}px`, overflow: 'hidden', background: '#000',
        border: '1px solid var(--dp-border-subtle)',
      }}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}`}
          title={title || 'Tutorial'}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div style={{
      width: '100%', maxWidth: '720px', margin: '0 auto', aspectRatio: '16 / 9', background: 'var(--dp-bg-secondary)',
      borderRadius: `${radius}px`, border: '1px solid var(--dp-border-subtle)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
    }}>
      <div style={{
        width: '44px', height: '44px', borderRadius: '50%',
        background: 'var(--dp-blue-subtle)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <PlayCircle size={24} style={{ color: 'var(--dp-blue-500)' }} />
      </div>
      <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>
        {loading ? 'Carregando...' : 'Vídeo em breve'}
      </div>
    </div>
  );
};

// ── Passo numerado ──
const NumberedStep = ({ n, color = THEME.blue, children }) => (
  <div style={{
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    background: 'var(--dp-bg-secondary)', border: '1px solid var(--dp-border-subtle)',
    borderRadius: 'var(--dp-radius-sm, 8px)', padding: '9px 12px',
  }}>
    <div style={{
      width: '20px', height: '20px', minWidth: '20px', borderRadius: '50%',
      background: color, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '11px', fontWeight: 600, marginTop: '1px',
    }}>
      {n}
    </div>
    <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', lineHeight: 1.5 }}>{children}</div>
  </div>
);

// ── Notas warning / danger ──
const WarningNote = ({ children }) => (
  <div style={{
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    padding: '12px 14px', marginTop: '14px',
    background: 'var(--dp-yellow-bg)',
    border: '1px solid rgba(251, 191, 36, 0.25)',
    borderRadius: 'var(--dp-radius-md, 12px)',
  }}>
    <AlertTriangle size={16} style={{ color: 'var(--dp-yellow-500)', flexShrink: 0, marginTop: '2px' }} />
    <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', lineHeight: 1.6 }}>{children}</div>
  </div>
);

const DangerNote = ({ children }) => (
  <div style={{
    display: 'flex', gap: '10px', alignItems: 'flex-start',
    padding: '12px 14px', marginTop: '10px',
    background: 'var(--dp-red-bg)',
    border: '1px solid rgba(248, 113, 113, 0.25)',
    borderRadius: 'var(--dp-radius-md, 12px)',
  }}>
    <ShieldAlert size={16} style={{ color: 'var(--dp-red-500)', flexShrink: 0, marginTop: '2px' }} />
    <div style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', lineHeight: 1.6 }}>{children}</div>
  </div>
);

// ── Card de download PREMIUM (padrão §6 do DESIGN_SYSTEM: sub-card #1A1A1A,
//    hover borda azul + lift; CTA canônico .dp-btn-cta--sm com seta) ──
const DownloadMiniCard = ({ icon, title, badgeText, badgeColor, badgeBg, sub, requisito, buttons, footer, delay = 0 }) => {
  return (
    <div
      style={{
        background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '14px', padding: '18px',
        display: 'flex', flexDirection: 'column', gap: '14px',
        transition: 'border-color .2s cubic-bezier(.4,0,.2,1), transform .2s cubic-bezier(.4,0,.2,1)',
        animation: `dp-fadeInUp .5s ease ${delay}s both`,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {/* Identidade da plataforma */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
          background: 'var(--dp-blue-subtle)', border: '1px solid rgba(13,174,255,0.22)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dp-text-primary)', letterSpacing: '-0.2px' }}>{title}</div>
          <div style={{ fontSize: '11.5px', color: 'var(--dp-text-tertiary)', marginTop: '2px' }}>{sub}</div>
        </div>
        <span style={{
          fontSize: '10.5px', fontWeight: 600, color: badgeColor, background: badgeBg,
          padding: '3px 9px', borderRadius: 'var(--dp-radius-full, 9999px)',
          whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          {badgeText}
        </span>
      </div>

      {/* Requisito (linha discreta que dá corpo ao card) */}
      {requisito && (
        <div style={{
          fontSize: '11.5px', color: 'var(--dp-text-tertiary)', lineHeight: 1.5,
          padding: '8px 10px', background: 'rgba(10,10,10,0.5)',
          border: '1px solid rgba(255,255,255,0.04)', borderRadius: '8px',
        }}>
          {requisito}
        </div>
      )}

      {/* CTA canônico + footer */}
      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {buttons.map((b) => (
          <a
            key={b.label}
            className="dp-btn-cta dp-btn-cta--sm"
            href={b.href || '#'}
            {...(b.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : { download: true })}
            style={{ width: '100%', textDecoration: 'none', boxSizing: 'border-box' }}
          >
            {b.icon} {b.label} <ArrowRight size={14} />
          </a>
        ))}
        <div style={{ minHeight: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {footer}
        </div>
      </div>
    </div>
  );
};

// ── Accordion (header + corpo) ──
const Accordion = ({ open, onToggle, icon, title, subtitle, badgeText, themeColor, children }) => (
  <div style={{
    background: open
      ? `linear-gradient(135deg, ${hexA(themeColor, 0.08)}, ${hexA(themeColor, 0.02)})`
      : 'var(--dp-bg-card)',
    border: `1px solid ${open ? hexA(themeColor, 0.35) : 'var(--dp-border-subtle)'}`,
    borderRadius: 'var(--dp-radius-md, 12px)',
    overflow: 'hidden',
    transition: 'border-color 200ms ease, background 200ms ease',
  }}>
    <div
      onClick={onToggle}
      style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', transition: 'background 0.2s ease' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{
        width: '32px', height: '32px', minWidth: '32px', borderRadius: 'var(--dp-radius-sm, 8px)',
        background: hexA(themeColor, 0.12),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>{title}</div>
        <div style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)' }}>{subtitle}</div>
      </div>
      <span style={{
        fontSize: '10px', color: themeColor, background: hexA(themeColor, 0.12),
        padding: '3px 8px', borderRadius: 'var(--dp-radius-sm, 8px)', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        {badgeText}
      </span>
      <ChevronDown
        size={18}
        style={{
          color: open ? themeColor : 'var(--dp-text-tertiary)',
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform 200ms ease',
          flexShrink: 0,
        }}
      />
    </div>
    {open && (
      <div style={{
        borderTop: `1px solid ${hexA(themeColor, 0.2)}`,
        padding: '16px', background: 'rgba(0,0,0,0.15)',
        animation: 'dp-pageFadeIn 0.25s ease',
      }}>
        {children}
      </div>
    )}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// MODAL DE CRIAÇÃO / EDIÇÃO DE ITEM
// ══════════════════════════════════════════════════════════════════════════════
const labelStyle = { fontSize: '12px', fontWeight: 500, color: 'var(--dp-text-secondary)', display: 'block', marginBottom: '4px' };

const SectionItemModal = ({ sectionKey, item, onClose, onSaved }) => {
  const isEdit = !!item;
  const [form, setForm] = useState({
    sidebar_title: item?.sidebar_title || '',
    sidebar_subtitle: item?.sidebar_subtitle || '',
    badge: item?.badge || '',
    title: item?.title || '',
    subtitle: item?.subtitle || '',
    indicated_for: item?.indicated_for || '',
    video_url: item?.video_url || '',
    warning: item?.warning || '',
    danger: item?.danger || '',
  });
  const [steps, setSteps] = useState(item?.steps?.length ? [...item.steps] : ['']);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.sidebar_title.trim()) { setError('O título da sidebar é obrigatório.'); return; }
    if (!form.title.trim()) { setError('O título é obrigatório.'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const body = { ...form, steps: steps.map((s) => s.trim()).filter(Boolean) };
      const url = isEdit
        ? `${API_BASE}/api/app-page/sections/item/${item.id}`
        : `${API_BASE}/api/app-page/sections/${sectionKey}`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const saved = await res.json();
      onSaved(saved);
    } catch (e) {
      setError(`Erro ao salvar: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        animation: 'dp-pageFadeIn 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="dp-thin-scrollbar"
        style={{
          background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-medium)',
          borderRadius: 'var(--dp-radius-lg, 16px)', width: '100%', maxWidth: '560px',
          maxHeight: '85vh', overflowY: 'auto', padding: '20px', fontFamily: 'var(--dp-font)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dp-text-primary)' }}>
            {isEdit ? 'Editar item' : 'Novo item'}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--dp-text-tertiary)', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Título da sidebar *</label>
            <input className="dp-input" value={form.sidebar_title} onChange={(e) => set('sidebar_title', e.target.value)} placeholder="Ex: Forma 1" />
          </div>
          <div>
            <label style={labelStyle}>Subtítulo da sidebar</label>
            <input className="dp-input" value={form.sidebar_subtitle} onChange={(e) => set('sidebar_subtitle', e.target.value)} placeholder="Ex: Perfil sem conta conectada" />
          </div>
          <div>
            <label style={labelStyle}>Badge</label>
            <input className="dp-input" value={form.badge} onChange={(e) => set('badge', e.target.value)} placeholder="Ex: FORMA 1" />
          </div>
          <div>
            <label style={labelStyle}>Título *</label>
            <input className="dp-input" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Título grande do item" />
          </div>
          <div>
            <label style={labelStyle}>Subtítulo / descrição</label>
            <textarea className="dp-input" value={form.subtitle} onChange={(e) => set('subtitle', e.target.value)} rows={2} style={{ resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>Indicado para</label>
            <textarea className="dp-input" value={form.indicated_for} onChange={(e) => set('indicated_for', e.target.value)} rows={2} style={{ resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>URL do vídeo (YouTube)</label>
            <input className="dp-input" value={form.video_url} onChange={(e) => set('video_url', e.target.value)} placeholder="Cole a URL do YouTube..." />
          </div>

          <div>
            <label style={labelStyle}>Passos</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input
                    className="dp-input"
                    value={s}
                    onChange={(e) => setSteps((arr) => arr.map((x, idx) => (idx === i ? e.target.value : x)))}
                    placeholder={`Passo ${i + 1}`}
                    style={{ flex: 1 }}
                  />
                  <button
                    onClick={() => setSteps((arr) => arr.filter((_, idx) => idx !== i))}
                    title="Remover passo"
                    style={{ background: 'transparent', border: '1px solid var(--dp-border-subtle)', borderRadius: 'var(--dp-radius-sm, 8px)', cursor: 'pointer', color: 'var(--dp-text-tertiary)', padding: '8px', display: 'flex' }}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setSteps((arr) => [...arr, ''])}
              style={{ marginTop: '8px', display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px dashed rgba(13,174,255,0.4)', borderRadius: 'var(--dp-radius-sm, 8px)', color: 'var(--dp-blue-500)', fontSize: '12px', fontWeight: 500, padding: '6px 12px', cursor: 'pointer' }}
            >
              <Plus size={14} /> Adicionar passo
            </button>
          </div>

          <div>
            <label style={labelStyle}>Aviso (warning)</label>
            <textarea className="dp-input" value={form.warning} onChange={(e) => set('warning', e.target.value)} rows={2} style={{ resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>Alerta (danger)</label>
            <textarea className="dp-input" value={form.danger} onChange={(e) => set('danger', e.target.value)} rows={2} style={{ resize: 'vertical' }} />
          </div>
        </div>

        {error && <div style={{ fontSize: '12px', color: 'var(--dp-red-500)', marginTop: '12px' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '18px' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', borderRadius: 'var(--dp-radius-sm, 8px)', border: '1px solid var(--dp-border-medium)', background: 'transparent', color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
          >
            Cancelar
          </button>
          <button
            className="dp-btn-cta dp-btn-cta--sm"
            onClick={save}
            disabled={saving}
          >
            {saving ? <Loader2 size={14} className="dp-spin" /> : <Save size={14} />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SECTION ACCORDION — sidebar dinâmica + painel + CRUD admin
// ══════════════════════════════════════════════════════════════════════════════
const SectionAccordion = ({ sectionKey, isOpen, onToggle, icon, themeColor, title, subtitle, sidebarLabel, isNarrow, isSuperAdmin }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/app-page/sections/${sectionKey}`);
      const data = res.ok ? await res.json() : [];
      const arr = Array.isArray(data) ? data : [];
      setItems(arr);
      setSelectedId((cur) => (cur && arr.some((d) => d.id === cur) ? cur : (arr[0] ? arr[0].id : null)));
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [sectionKey]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const selected = items.find((i) => i.id === selectedId) || null;
  const badgeText = loading ? '...' : (items.length > 0 ? `${items.length} ${items.length === 1 ? 'item' : 'itens'}` : 'Em breve');
  const videoId = selected && selected.video_url ? extractYouTubeId(selected.video_url) : null;

  const openCreate = () => { setEditingItem(null); setModalOpen(true); };
  const openEdit = (it) => { setEditingItem(it); setModalOpen(true); };

  const handleDelete = async (it) => {
    if (typeof window !== 'undefined' && !window.confirm(`Excluir "${it.sidebar_title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/app-page/sections/item/${it.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await loadItems();
    } catch (e) {
      window.alert(`Erro ao excluir: ${e.message}`);
    }
  };

  const handleSaved = async () => { setModalOpen(false); setEditingItem(null); await loadItems(); };

  const THEME_GRADIENTS = {
    '#0DAEFF': 'linear-gradient(135deg, #0DAEFF, #3B82F6)',
    '#FB923C': 'linear-gradient(135deg, #FB923C, #F97316)',
    '#34D399': 'linear-gradient(135deg, #34D399, #10B981)',
  };
  const THEME_ACCENTS = { '#0DAEFF': '#0DAEFF', '#FB923C': '#F97316', '#34D399': '#10B981' };
  const activeGradient = THEME_GRADIENTS[themeColor] || THEME_GRADIENTS['#0DAEFF'];
  const accent = THEME_ACCENTS[themeColor] || themeColor;

  // Ícone editar/deletar — quadrado 28x28, sem glow; transform/boxShadow none
  // anulam o efeito global de hover de <button> do index.css.
  const iconBtnBase = {
    width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.04)', border: 'none', borderRadius: '6px',
    cursor: 'pointer', color: '#9CA3AF', boxShadow: 'none', transform: 'none', flexShrink: 0,
    transition: '0.2s ease',
  };

  const renderNavItem = (it) => {
    const active = it.id === selectedId;
    return (
      <div
        key={it.id}
        onClick={() => setSelectedId(it.id)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer',
          padding: '12px', borderRadius: 'var(--dp-radius-md, 12px)',
          border: active ? `1px solid ${hexA(themeColor, 0.4)}` : '1px solid var(--dp-border-subtle)',
          background: active ? activeGradient : 'var(--dp-bg-card)',
          transition: '0.2s ease',
        }}
        onMouseEnter={(e) => { if (active) return; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
        onMouseLeave={(e) => { if (active) return; e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: active ? '#fff' : 'var(--dp-text-primary)' }}>{it.sidebar_title}</div>
          {it.sidebar_subtitle && (
            <div style={{ fontSize: '12px', marginTop: '2px', color: active ? 'rgba(255,255,255,0.85)' : 'var(--dp-text-tertiary)' }}>{it.sidebar_subtitle}</div>
          )}
        </div>
        {isSuperAdmin && (
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
            <button
              title="Editar"
              onClick={(e) => { e.stopPropagation(); openEdit(it); }}
              style={iconBtnBase}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
            ><Pencil size={13} /></button>
            <button
              title="Excluir"
              onClick={(e) => { e.stopPropagation(); handleDelete(it); }}
              style={iconBtnBase}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#EF4444'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#9CA3AF'; }}
            ><Trash2 size={13} /></button>
          </div>
        )}
      </div>
    );
  };

  const addButton = isSuperAdmin ? (
    <button
      onClick={openCreate}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', width: '100%',
        padding: '10px', borderRadius: '8px', textAlign: 'center', cursor: 'pointer',
        background: 'transparent', border: `1px dashed ${hexA(accent, 0.3)}`, color: accent,
        fontSize: '13px', fontWeight: 500, fontFamily: 'var(--dp-font)',
        boxShadow: 'none', transform: 'none', transition: '0.2s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.border = `1px solid ${hexA(accent, 0.5)}`; e.currentTarget.style.background = hexA(accent, 0.05); }}
      onMouseLeave={(e) => { e.currentTarget.style.border = `1px dashed ${hexA(accent, 0.3)}`; e.currentTarget.style.background = 'transparent'; }}
    >
      <Plus size={14} /> Adicionar item
    </button>
  ) : null;

  const emptyMsg = (
    <div style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', padding: '4px 0' }}>
      {isSuperAdmin ? 'Nenhum item ainda. Adicione o primeiro.' : 'Conteúdo em breve'}
    </div>
  );

  const sidebar = (
    <div>
      <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '1.2px', color: 'var(--dp-text-tertiary)', textTransform: 'uppercase', marginBottom: '10px' }}>{sidebarLabel}</div>
      {isNarrow ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {items.length > 0 && (
            <select className="dp-input" value={selectedId || ''} onChange={(e) => setSelectedId(e.target.value)}>
              {items.map((it) => (
                <option key={it.id} value={it.id}>{it.sidebar_title}{it.sidebar_subtitle ? ` — ${it.sidebar_subtitle}` : ''}</option>
              ))}
            </select>
          )}
          {isSuperAdmin && selected && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => openEdit(selected)}
                style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', borderRadius: '6px', border: 'none', background: 'rgba(255,255,255,0.04)', color: '#9CA3AF', cursor: 'pointer', fontSize: '12px', boxShadow: 'none', transform: 'none', transition: '0.2s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              ><Pencil size={13} /> Editar</button>
              <button
                onClick={() => handleDelete(selected)}
                style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', borderRadius: '6px', border: 'none', background: 'rgba(255,255,255,0.04)', color: '#9CA3AF', cursor: 'pointer', fontSize: '12px', boxShadow: 'none', transform: 'none', transition: '0.2s ease' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#EF4444'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.color = '#9CA3AF'; }}
              ><Trash2 size={13} /> Excluir</button>
            </div>
          )}
          {addButton}
          {items.length === 0 && emptyMsg}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {items.map(renderNavItem)}
          {addButton}
          {items.length === 0 && emptyMsg}
        </div>
      )}
    </div>
  );

  const panel = (
    <div style={{ minWidth: 0 }}>
      {selected ? (
        <>
          {selected.badge && <div style={{ marginBottom: '10px' }}><Badge color={THEME.blue}>{selected.badge}</Badge></div>}
          {selected.title && <h2 style={{ fontSize: '24px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '0 0 8px', lineHeight: 1.2 }}>{selected.title}</h2>}
          {selected.subtitle && <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>{selected.subtitle}</p>}
          {selected.indicated_for && (
            <div style={{ ...CARD_STYLE, background: 'var(--dp-bg-secondary)', padding: '14px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--dp-blue-500)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>Indicado para:</div>
              <div style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', lineHeight: 1.6 }}>{selected.indicated_for}</div>
            </div>
          )}
          {selected.video_url && (
            <div style={{ marginBottom: '16px' }}>
              <VideoFrame videoId={videoId} loading={false} title={selected.title} radius={8} />
            </div>
          )}
          {selected.steps && selected.steps.length > 0 && (
            <>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BookOpen size={16} style={{ color: accent }} /> Passo a passo
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {selected.steps.map((s, i) => <NumberedStep key={i} n={i + 1} color={accent}>{s}</NumberedStep>)}
              </div>
            </>
          )}
          {selected.warning && <WarningNote>{selected.warning}</WarningNote>}
          {selected.danger && <DangerNote>{selected.danger}</DangerNote>}
        </>
      ) : (
        <div style={{ ...CARD_STYLE, background: 'var(--dp-bg-secondary)', padding: '32px', textAlign: 'center' }}>
          <Sparkles size={36} style={{ color: themeColor, opacity: 0.5 }} />
          <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--dp-text-primary)', marginTop: '10px' }}>
            {isSuperAdmin ? 'Nenhum item ainda' : 'Conteúdo em breve'}
          </div>
          <p style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', maxWidth: '440px', margin: '8px auto 0', lineHeight: 1.6 }}>
            {isSuperAdmin ? 'Use "Adicionar item" para criar o primeiro conteúdo desta seção.' : 'Em breve esta seção terá conteúdo. Volte mais tarde.'}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <Accordion open={isOpen} onToggle={onToggle} icon={icon} title={title} subtitle={subtitle} badgeText={badgeText} themeColor={themeColor}>
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--dp-text-tertiary)', fontSize: '13px', padding: '8px' }}>
          <Loader2 size={16} className="dp-spin" /> Carregando...
        </div>
      ) : (
        <div style={{ display: isNarrow ? 'block' : 'grid', gridTemplateColumns: isNarrow ? undefined : '280px 1fr', gap: '20px' }}>
          {sidebar}
          {panel}
        </div>
      )}
      {modalOpen && (
        <SectionItemModal
          sectionKey={sectionKey}
          item={editingItem}
          onClose={() => { setModalOpen(false); setEditingItem(null); }}
          onSaved={handleSaved}
        />
      )}
    </Accordion>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const DarkPlannerAppPage = ({ onLogout }) => {
  const { sidebarWidth, isMobile } = useSidebar();
  const { isSuperAdmin } = useAdmin();
  const navigate = useNavigate();
  const macInfo = useMacReleaseInfo();
  const winInfo = useWindowsReleaseInfo();

  // Guia único da página (Download e Instalação) — abre JÁ EXPANDIDO.
  // Guia de Conexão e Como Usar saíram daqui: os tutoriais de uso vivem na
  // Academia (botão "Como usar o app" + aviso obrigatório abaixo).
  const [openAccordion, setOpenAccordion] = useState('install');
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 900 : false
  );

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const toggleAccordion = (key) => setOpenAccordion((cur) => (cur === key ? null : key));

  const downloadCards = [
    {
      title: 'Windows',
      icon: <Monitor size={20} style={{ color: 'var(--dp-blue-500)' }} />,
      badgeText: winInfo.loading ? '...' : `v${winInfo.version}`,
      badgeColor: 'var(--dp-green-500)', badgeBg: 'var(--dp-green-bg)',
      sub: 'App Desktop',
      requisito: 'Windows 10 ou superior · instalador .exe',
      delay: 0.05,
      buttons: [
        { label: 'Baixar para Windows', icon: <Download size={14} />, href: winInfo.downloadUrl, newTab: false },
      ],
    },
    {
      title: 'macOS',
      icon: <AppleGlyph size={19} color="var(--dp-blue-500)" />,
      badgeText: macInfo.loading ? '...' : `v${macInfo.version}`,
      badgeColor: 'var(--dp-green-500)', badgeBg: 'var(--dp-green-bg)',
      sub: 'App Desktop',
      requisito: 'Apple Silicon (M1, M2, M3, M4) · instalador .dmg',
      delay: 0.1,
      buttons: [
        { label: 'Baixar para macOS', icon: <Download size={14} />, href: macInfo.arm64Url, newTab: false },
      ],
      footer: (
        <a
          href={macInfo.x64Url}
          download
          style={{
            fontSize: '11px', color: 'var(--dp-text-secondary)', cursor: 'pointer',
            textDecoration: 'none', textAlign: 'center', display: 'block',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; e.currentTarget.style.color = 'var(--dp-blue-500)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
        >
          Tem Mac Intel? Clique aqui
        </a>
      ),
    },
    {
      title: 'Extensão Chrome',
      icon: <Puzzle size={20} style={{ color: 'var(--dp-blue-500)' }} />,
      badgeText: 'Essencial',
      badgeColor: 'var(--dp-blue-500)', badgeBg: 'rgba(13, 174, 255, 0.10)',
      sub: 'Chrome Web Store',
      requisito: 'Obrigatória junto com o app: é ela que conecta o navegador à automação.',
      delay: 0.15,
      buttons: [
        { label: 'Instalar extensão', icon: <ExternalLink size={14} />, href: 'https://chromewebstore.google.com/detail/darkplanner/dkpcmhcjlidhmmnileiojcflhpclhcdn', newTab: true },
      ],
    },
  ];

  // Fluxo de ativação (mostrado abaixo dos downloads) — dá ordem clara ao usuário
  const flowSteps = [
    'Baixe e instale o App Desktop',
    'Instale a Extensão no Chrome',
    'Assista aos tutoriais na Academia',
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />
      <Header />

      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        transition: 'margin-left 0.3s ease',
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        fontFamily: 'var(--dp-font)',
      }}>
        <PageHeader
          icon={<Monitor size={20} style={{ color: 'var(--dp-blue-500)' }} />}
          title="DarkPlanner App"
          description="Automatize a geração de vídeos e imagens com IA"
        />

        {/* ═══ HERO DE DOWNLOADS — card mestre com barra de acento + atmosfera ═══ */}
        <section
          className="dp-top-accent"
          style={{
            position: 'relative', overflow: 'hidden',
            background: '#141414', border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '20px', padding: isNarrow ? '18px' : '24px',
            marginBottom: '16px', animation: 'dp-fadeInUp .45s ease both',
          }}
        >
          {/* Atmosfera: brilho radial azul no canto (profundidade, sem "IA genérica") */}
          <div style={{
            position: 'absolute', top: '-160px', right: '-120px', width: '480px', height: '480px',
            borderRadius: '50%', background: 'radial-gradient(circle, rgba(13,174,255,0.07), transparent 65%)',
            pointerEvents: 'none',
          }} />

          {/* Cabeçalho da seção */}
          <div style={{
            position: 'relative', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            gap: '12px', flexWrap: 'wrap', marginBottom: '18px',
          }}>
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '1.2px', color: 'var(--dp-blue-500)', textTransform: 'uppercase', marginBottom: '5px' }}>
                Downloads oficiais
              </div>
              <div style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: 'var(--dp-text-primary)' }}>
                Instale o app e a extensão
              </div>
            </div>
            <span style={{
              fontSize: '11.5px', fontWeight: 600, color: 'var(--dp-blue-500)',
              background: 'rgba(13,174,255,0.08)', border: '1px solid rgba(13,174,255,0.22)',
              padding: '5px 12px', borderRadius: 'var(--dp-radius-full, 9999px)', whiteSpace: 'nowrap',
            }}>
              App Desktop + Extensão · os dois são necessários
            </span>
          </div>

          {/* Cards de plataforma */}
          <div
            className="cq-grid-3"
            style={{ position: 'relative', display: 'grid', gridTemplateColumns: isNarrow ? '1fr' : 'repeat(3, 1fr)', gap: '12px', alignItems: 'stretch' }}
          >
            {downloadCards.map((c) => (
              <DownloadMiniCard key={c.title} {...c} />
            ))}
          </div>

          {/* Fluxo de ativação: 1 → 2 → 3 (ordem clara, ninguém se perde) */}
          <div style={{
            position: 'relative', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
            marginTop: '16px', padding: '13px 16px',
            background: 'rgba(10,10,10,0.55)', border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: '12px',
          }}>
            {flowSteps.map((s, i) => (
              <React.Fragment key={s}>
                {i > 0 && <ArrowRight size={13} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0 }} />}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: 'var(--dp-text-secondary)', whiteSpace: 'nowrap' }}>
                  <span style={{
                    width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg, #0DAEFF, #3B82F6)', color: '#fff',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: 700,
                  }}>
                    {i + 1}
                  </span>
                  {s}
                </span>
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* ═══ GATE OBRIGATÓRIO — assistir os vídeos de uso ANTES de usar o app ═══ */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
          background: 'linear-gradient(135deg, rgba(251,191,36,0.10), rgba(251,191,36,0.03))',
          border: '1px solid rgba(251, 191, 36, 0.28)',
          borderLeft: '4px solid var(--dp-yellow-500)',
          borderRadius: '16px',
          padding: isNarrow ? '16px' : '18px 22px', marginBottom: '24px',
          animation: 'dp-fadeInUp .5s ease .2s both',
        }}>
          <div style={{
            width: '46px', height: '46px', borderRadius: '12px', flexShrink: 0,
            background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertTriangle size={22} style={{ color: 'var(--dp-yellow-500)' }} />
          </div>
          <div style={{ flex: '1 1 300px', minWidth: 0 }}>
            <div style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '-0.2px', color: 'var(--dp-text-primary)', lineHeight: 1.35, marginBottom: '4px' }}>
              OBRIGATÓRIO: assista aos vídeos de como usar o app antes de iniciar o uso
            </div>
            <div style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', lineHeight: 1.6 }}>
              Os tutoriais completos (conexão, geração de imagem, vídeo e áudio) estão na Academia.
              Assistir antes evita erros de configuração e travamentos nas suas primeiras gerações.
            </div>
          </div>
          <button
            className="dp-btn-cta"
            onClick={() => navigate('/academia')}
            style={{ flexShrink: 0 }}
          >
            <PlayCircle size={16} /> Como usar o app <ArrowRight size={16} />
          </button>
        </div>

        {/* Guia único: Download e Instalação (já aberto) */}
        <div style={{ animation: 'dp-fadeInUp .5s ease .3s both' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '1.2px', color: 'var(--dp-blue-500)', textTransform: 'uppercase', marginBottom: '10px' }}>
            Guia passo a passo
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <SectionAccordion
              sectionKey="install"
              isOpen={openAccordion === 'install'}
              onToggle={() => toggleAccordion('install')}
              icon={<Download size={16} style={{ color: THEME.blue }} />}
              themeColor={THEME.blue}
              title="Download e Instalação"
              subtitle="Como baixar e instalar o App + Extensão"
              sidebarLabel="Etapas"
              isNarrow={isNarrow}
              isSuperAdmin={isSuperAdmin}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default DarkPlannerAppPage;
